import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const MENU_Z = 'z-[13000]';
const MENU_MAX_HEIGHT = 224;

/**
 * Educational qualification — field-with-label + select look; checkboxes only in dropdown.
 * Menu is portaled above modals (z-index > ModalShell).
 */
export default function QualificationMultiSelect({
  options = [],
  value = [],
  onChange,
  label = 'Educational Qualification',
  placeholder = 'Select qualification',
  /** Noun used in summary — e.g. "2 qualifications selected" or "2 age groups selected" */
  countLabelSingular = 'qualification',
  countLabelPlural = 'qualifications'
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 280 });
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const selected = Array.isArray(value) ? value.filter(Boolean) : [];

  const updatePosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const menuHeight = Math.min(menuRef.current?.offsetHeight || MENU_MAX_HEIGHT, MENU_MAX_HEIGHT);
    const gap = 4;
    const pad = 8;
    let top = rect.bottom + gap;
    if (top + menuHeight > window.innerHeight - pad) {
      const above = rect.top - menuHeight - gap;
      if (above >= pad) top = above;
    }
    setPos({
      top,
      left: Math.max(pad, Math.min(rect.left, window.innerWidth - rect.width - pad)),
      width: Math.max(rect.width, 280)
    });
  }, []);

  useEffect(() => {
    if (!open) return;

    updatePosition();
    const raf = requestAnimationFrame(updatePosition);

    const onScroll = () => updatePosition();
    const onResize = () => updatePosition();
    const onKey = e => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onPointerDown = e => {
      if (triggerRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };

    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointerDown);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [open, updatePosition, selected.length]);

  const toggle = option => {
    const next = selected.includes(option)
      ? selected.filter(s => s !== option)
      : [...selected, option];
    onChange(next);
  };

  const displayValue =
    selected.length === 0
      ? ''
      : selected.length === 1
        ? selected[0]
        : `${selected.length} ${selected.length === 1 ? countLabelSingular : countLabelPlural} selected`;

  const selectionTitle = selected.length > 1 ? selected.join('\n') : undefined;

  const filled = selected.length > 0 || open;

  const menu =
    open &&
    createPortal(
      <div
        ref={menuRef}
        role="listbox"
        aria-multiselectable="true"
        className={`fixed ${MENU_Z} border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-xl overflow-y-auto`}
        style={{
          top: pos.top,
          left: pos.left,
          width: pos.width,
          maxHeight: MENU_MAX_HEIGHT
        }}
        onMouseDown={e => e.preventDefault()}
      >
        {options.length === 0 ? (
          <p className="px-3 py-2 text-sm text-[hsl(var(--ds-muted-fg))]">No options</p>
        ) : (
          options.map(option => {
            const checked = selected.includes(option);
            return (
              <label
                key={option}
                className={`flex items-start gap-2.5 px-3 py-2.5 text-sm cursor-pointer hover:bg-[hsl(var(--ds-primary)/0.08)] border-b border-[hsl(var(--ds-border))] last:border-0 ${
                  checked ? 'bg-[hsl(var(--ds-primary)/0.06)]/90' : ''
                }`}
                onClick={e => {
                  e.preventDefault();
                  toggle(option);
                }}
              >
                <input
                  type="checkbox"
                  className="mt-0.5 shrink-0 pointer-events-none"
                  checked={checked}
                  readOnly
                  tabIndex={-1}
                />
                <span className="text-[hsl(var(--ds-fg))] leading-snug">{option}</span>
              </label>
            );
          })
        )}
      </div>,
      document.body
    );

  return (
    <div
      className={`field-with-label relative ${filled ? 'qualification-filled' : ''} ${open ? 'qualification-open' : ''}`}
      ref={rootRef}
    >
      <button
        ref={triggerRef}
        type="button"
        className={`qualification-multiselect-trigger h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))] text-left bg-[hsl(var(--ds-surface))] flex items-center justify-between gap-2 ${
          selected.length === 0 ? 'text-[hsl(var(--ds-muted-fg))]' : 'text-[hsl(var(--ds-fg))]'
        }`}
        onMouseDown={e => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onClick={e => {
          e.stopPropagation();
          setOpen(o => !o);
        }}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span className="truncate block flex-1 min-w-0" title={selectionTitle}>
          {selected.length === 0 ? placeholder : displayValue}
        </span>
        <span className="text-[hsl(var(--ds-muted-fg))] shrink-0 text-[10px] leading-none" aria-hidden="true">
          {open ? '▴' : '▾'}
        </span>
      </button>
      <label
        onMouseDown={e => {
          e.preventDefault();
          triggerRef.current?.click();
        }}
      >
        {label}
      </label>
      {menu}
    </div>
  );
}
